import React from 'react';
import { XIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, UserIcon, CalendarIcon, ClockIcon, LinkIcon, ExternalLinkIcon, BuildingIcon, InfoIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, HashIcon, TagIcon } from 'lucide-react';
import { LedgerEntry, DocumentType, LedgerState } from './LedgerView';
import { formatAmount } from '../utils/currencyFormatter';
interface LedgerEntryDetailProps {
  entry: LedgerEntry;
  onClose: () => void;
  locale: string;
}
export function LedgerEntryDetail({
  entry,
  onClose,
  locale
}: LedgerEntryDetailProps) {
  // Get document type details
  const getDocumentTypeDetails = (type: DocumentType) => {
    switch (type) {
      case 'Invoice':
        return {
          icon: <ReceiptIcon className="h-5 w-5" data-id="element-10146" />,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
          label: 'Invoice'
        };
      case 'Purchase Order':
        return {
          icon: <ShoppingCartIcon className="h-5 w-5" data-id="element-10147" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          label: 'Purchase Order'
        };
      case 'Credit Note':
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-10148" />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-100',
          label: 'Credit Note'
        };
      case 'Expense Claim':
        return {
          icon: <CreditCardIcon className="h-5 w-5" data-id="element-10149" />,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          label: 'Expense Claim'
        };
      case 'Contract':
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-10150" />,
          color: 'text-indigo-600',
          bgColor: 'bg-indigo-100',
          label: 'Contract'
        };
      case 'Payment':
        return {
          icon: <DollarSignIcon className="h-5 w-5" data-id="element-10151" />,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-100',
          label: 'Payment'
        };
      default:
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-10152" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          label: 'Document'
        };
    }
  };
  const documentDetails = getDocumentTypeDetails(entry.documentType);
  // Get ledger state details
  const getLedgerStateDetails = (state: LedgerState) => {
    switch (state) {
      case 'Informational':
        return {
          icon: <InfoIcon className="h-5 w-5" data-id="element-10153" />,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          label: 'Informational',
          description: 'Draft entry - not yet impacting financial exposure'
        };
      case 'Active':
        return {
          icon: <CheckCircleIcon className="h-5 w-5" data-id="element-10154" />,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          label: 'Active',
          description: 'Active entry impacting financial exposure'
        };
      case 'Reversed':
        return {
          icon: <XCircleIcon className="h-5 w-5" data-id="element-10155" />,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          label: 'Reversed',
          description: 'Entry has been reversed'
        };
      default:
        return {
          icon: <InfoIcon className="h-5 w-5" data-id="element-10156" />,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          label: state,
          description: ''
        };
    }
  };
  const stateDetails = getLedgerStateDetails(entry.ledgerState);
  const isReversal = entry.ledgerEntryType === 'Reversal';
  return <div className="fixed inset-0 overflow-hidden z-20" aria-labelledby="slide-over-title" role="dialog" aria-modal="true" data-id="element-10157">
      <div className="absolute inset-0 overflow-hidden" data-id="element-10158">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-10159"></div>
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-10160">
          <div className="relative w-screen max-w-2xl" data-id="element-10161">
            <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-auto" data-id="element-10162">
              {/* Header */}
              <div className="px-4 sm:px-6" data-id="element-10163">
                <div className="flex items-start justify-between" data-id="element-10164">
                  <h2 className="text-lg font-medium text-gray-900" id="slide-over-title" data-id="element-10165">
                    Operational Ledger Entry
                  </h2>
                  <div className="ml-3 h-7 flex items-center" data-id="element-10166">
                    <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={onClose} data-id="element-10167">
                      <span className="sr-only" data-id="element-10168">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-10169" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="mt-6 relative flex-1 px-4 sm:px-6" data-id="element-10170">
                {/* Entry ID and State */}
                <div className="border-b border-gray-200 pb-5 mb-5" data-id="element-10171">
                  <div className="flex justify-between items-start" data-id="element-10172">
                    <div data-id="element-10173">
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-10174">
                        {entry.documentNumber}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 font-mono" data-id="element-10175">
                        Entry ID: {entry.ledgerEntryId}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stateDetails.bgColor} ${stateDetails.textColor}`} data-id="element-10176">
                      {stateDetails.icon}
                      <span className="ml-1" data-id="element-10177">{stateDetails.label}</span>
                    </span>
                  </div>
                  {stateDetails.description && <p className="mt-2 text-sm text-gray-600" data-id="element-10178">
                      {stateDetails.description}
                    </p>}
                </div>

                {/* Reversal Warning */}
                {isReversal && entry.reversesLedgerEntryId && <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4" data-id="element-10179">
                    <div className="flex" data-id="element-10180">
                      <AlertCircleIcon className="h-5 w-5 text-orange-400" data-id="element-10181" />
                      <div className="ml-3" data-id="element-10182">
                        <h3 className="text-sm font-medium text-orange-800" data-id="element-10183">
                          Reversal Entry
                        </h3>
                        <p className="mt-1 text-sm text-orange-700" data-id="element-10184">
                          This entry reverses ledger entry:{' '}
                          <span className="font-mono" data-id="element-10185">
                            {entry.reversesLedgerEntryId}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>}

                {/* Document information */}
                <div className="mb-6" data-id="element-10186">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10187">
                    DOCUMENT INFORMATION
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10188">
                    <div className="flex items-center mb-3" data-id="element-10189">
                      <div className={`p-2 rounded-md ${documentDetails.bgColor} ${documentDetails.color} mr-3`} data-id="element-10190">
                        {documentDetails.icon}
                      </div>
                      <div data-id="element-10191">
                        <p className="text-sm font-medium text-gray-900" data-id="element-10192">
                          {documentDetails.label}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-10193">
                          Status: {entry.documentStatus}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-10194">
                      <div data-id="element-10195">
                        <p className="text-xs text-gray-500" data-id="element-10196">Document ID</p>
                        <p className="font-mono text-gray-900" data-id="element-10197">
                          {entry.documentId}
                        </p>
                      </div>
                      <div data-id="element-10198">
                        <p className="text-xs text-gray-500" data-id="element-10199">Entry Type</p>
                        <p className="text-gray-900" data-id="element-10200">{entry.ledgerEntryType}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center" data-id="element-10201">
                      <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10202" />
                      <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-10203">
                        View Document{' '}
                        <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-10204" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Organisational Context */}
                <div className="mb-6" data-id="element-10205">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10206">
                    ORGANISATIONAL CONTEXT
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10207">
                    <div className="flex items-start mb-2" data-id="element-10208">
                      <BuildingIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" data-id="element-10209" />
                      <div className="flex-1" data-id="element-10210">
                        <p className="text-xs text-gray-500" data-id="element-10211">Org Unit</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10212">
                          {entry.orgUnitName}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-10213">
                          Path: {entry.orgUnitPath}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                <div className="mb-6" data-id="element-10214">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10215">
                    FINANCIAL IMPACT
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10216">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-10217">
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200" data-id="element-10218">
                        <p className="text-xs text-green-600 font-medium mb-1" data-id="element-10219">
                          Debit (Increases Exposure)
                        </p>
                        <p className="text-xl font-mono font-bold text-green-700" data-id="element-10220">
                          {entry.debitAmount ? formatAmount(entry.debitAmount, locale) : '-'}
                        </p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 border border-red-200" data-id="element-10221">
                        <p className="text-xs text-red-600 font-medium mb-1" data-id="element-10222">
                          Credit (Reduces Exposure)
                        </p>
                        <p className="text-xl font-mono font-bold text-red-700" data-id="element-10223">
                          {entry.creditAmount ? formatAmount(entry.creditAmount, locale) : '-'}
                        </p>
                      </div>
                    </div>
                    {entry.calculatedRunningBalance !== undefined && <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200" data-id="element-10224">
                        <p className="text-xs text-indigo-600 font-medium mb-1" data-id="element-10225">
                          Running Balance (Calculated)
                        </p>
                        <p className="text-xl font-mono font-bold text-indigo-700" data-id="element-10226">
                          {formatAmount(entry.calculatedRunningBalance, locale)}
                        </p>
                        <p className="text-xs text-indigo-600 mt-1" data-id="element-10227">
                          Display Precision: {entry.displayPrecision} decimals
                        </p>
                      </div>}
                  </div>
                </div>

                {/* GL Coding */}
                <div className="mb-6" data-id="element-10228">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10229">
                    GL CODING & ALLOCATION
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-10230">
                    <div className="flex items-center" data-id="element-10231">
                      <HashIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10232" />
                      <div data-id="element-10233">
                        <p className="text-xs text-gray-500" data-id="element-10234">GL Code</p>
                        <p className="text-sm font-mono font-medium text-gray-900" data-id="element-10235">
                          {entry.glCode}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counterparty */}
                {(entry.vendorName || entry.userName) && <div className="mb-6" data-id="element-10236">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10237">
                      COUNTERPARTY
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-10238">
                      <div className="flex items-center" data-id="element-10239">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10240" />
                        <div data-id="element-10241">
                          <p className="text-xs text-gray-500" data-id="element-10242">
                            {entry.vendorName ? 'Vendor' : 'User'}
                          </p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-10243">
                            {entry.vendorName || entry.userName}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Audit and Traceability */}
                <div className="mb-6" data-id="element-10244">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10245">
                    AUDIT & TRACEABILITY
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-id="element-10246">
                    <div className="flex items-center" data-id="element-10247">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10248" />
                      <div data-id="element-10249">
                        <p className="text-xs text-gray-500" data-id="element-10250">Created Date</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10251">
                          {new Date(entry.createdDate).toLocaleString(locale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-10252">
                      <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10253" />
                      <div data-id="element-10254">
                        <p className="text-xs text-gray-500" data-id="element-10255">Created By</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10256">
                          {entry.createdBy}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-10257">
                      <TagIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10258" />
                      <div data-id="element-10259">
                        <p className="text-xs text-gray-500" data-id="element-10260">Source System</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-10261">
                          {entry.sourceSystem}
                        </p>
                      </div>
                    </div>
                    {entry.erpReference && <div className="flex items-center" data-id="element-10262">
                        <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-10263" />
                        <div data-id="element-10264">
                          <p className="text-xs text-gray-500" data-id="element-10265">ERP Reference</p>
                          <p className="text-sm font-mono font-medium text-gray-900" data-id="element-10266">
                            {entry.erpReference}
                          </p>
                        </div>
                      </div>}
                  </div>
                </div>

                {/* Notes */}
                {entry.reasonOrNotes && <div className="mb-6" data-id="element-10267">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-10268">
                      REASON / NOTES
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-10269">
                      <p className="text-sm text-gray-900" data-id="element-10270">
                        {entry.reasonOrNotes}
                      </p>
                    </div>
                  </div>}

                {/* Read-only Notice */}
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4" data-id="element-10271">
                  <div className="flex" data-id="element-10272">
                    <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-10273" />
                    <div className="ml-3" data-id="element-10274">
                      <p className="text-sm text-blue-700" data-id="element-10275">
                        <strong data-id="element-10276">Read-Only Ledger:</strong> This entry is
                        append-only and cannot be modified. Corrections are made
                        through reversing entries.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}